import type { Zone } from './crashChipLogic'

// ── Refresh persistence (ASK 2 — v15) ────────────────────────────────────
// On a page refresh with a bet active, the crash panel used to go dark: the
// riding bet, its stake, the live cash-out control and the win/busted card
// all vanished — even though the bet IS back in the store (the page-mount
// fetchBets → setBets fill repopulates it, and crash bet ids are
// DETERMINISTIC so the refetched id matches the one the client minted).
//
// The reason: the panel's display is keyed off `myBetIdRef` (a per-mount
// useRef, undefined on load) and two useState snapshots (submittedStakes /
// submittedCashout) that all reset to empty on mount and were only ever
// written by a live submit. Nothing reconstructed them from the store.
//
// This pure resolver mirrors ChipPlacementPanel's `currentBets → placedChips`
// restore effect (ChipPlacementPanel.tsx:485-593): it rebuilds the id→zone
// binding + the per-zone submitted stake PURELY from the server bets in the
// current race, so a store re-fill after refresh reconstructs the board with
// no client-side handshake.
//
// Zone assignment: the client tags the RIGHT zone's bet with
// `betDetails.nonce === 'slot1'` (useCrashBetSubmit slotIndex 1); the LEFT
// zone carries no nonce. That is the ONLY zone signal that survives on the
// BetDTO snapshot, so it is what we key off.
//
// GENUINELY UNRECOVERABLE (documented, handled by the caller): the ARMED
// auto-cashout target multiplier (`submittedCashout`). BetDTO carries no
// `autoCashoutMultiplier` (it is send-only, stripped by fromBetDTO), so a
// still-riding auto-cashout bet cannot re-show its locked target pill after a
// refresh. Auto-cashout is enforced SERVER-SIDE and still fires; the bet
// itself reappears (stake + live value + a working manual cash-out button).
// A CASHED bet still shows its realized multiplier because `cashoutMultiplier`
// IS on the snapshot (drives resolveZoneWinBanner's first fallback).

// A cancelled bet was refunded — the zone is free again, so it must NOT be
// rehydrated (mirrors resolveZoneRiding's RIDING_FREE for cancelled, and the
// card resolver treats a cancelled zone as empty). Every other status
// (pending / won / lost / cashed_out) drives a visible card and IS restored.
const REHYDRATE_SKIP_STATUSES = ['cancelled']

// The minimal shape the resolver reads off a domain Bet (structurally a
// subset of Bet, so `Bet[]` passes without a cast).
export interface RehydratableBet {
  id: string
  betAmount: number
  status: string
  createdAt: string
  // Only `nonce` is read (zone assignment); optional so a raw/partial bet is
  // tolerated. Structurally a subset of the domain BetDetails union.
  betDetails?: { nonce?: string } | null
}

export interface RehydratedZoneBet {
  id: string
  stake: number
}

export type RehydratedZoneBets = Record<Zone, RehydratedZoneBet | undefined>

const zoneOf = (bet: RehydratableBet): Zone =>
  bet.betDetails?.nonce === 'slot1' ? 'right' : 'left'

/**
 * Reconstruct the per-zone bet binding (id + submitted stake) from the
 * current-race server bets, so a post-refresh store re-fill restores the
 * crash board. Pure + deterministic: when two bets somehow map to the same
 * zone, the EARLIEST (by createdAt) wins so the choice does not depend on
 * store ordering — matching ChipPlacementPanel's createdAt sort.
 *
 * `currentBets` is expected to be pre-filtered to the current race (that is
 * what useGetCurrentBets returns); this resolver does not re-filter by race.
 */
export function resolveRehydratedZoneBets(
  currentBets: readonly RehydratableBet[]
): RehydratedZoneBets {
  const ordered = [...currentBets].sort((a, b) => {
    const ta = a.createdAt ?? ''
    const tb = b.createdAt ?? ''
    return ta < tb ? -1 : ta > tb ? 1 : 0
  })

  const out: RehydratedZoneBets = { left: undefined, right: undefined }
  for (const bet of ordered) {
    if (REHYDRATE_SKIP_STATUSES.includes(bet.status)) continue
    const zone = zoneOf(bet)
    // Earliest wins — first assignment per zone sticks.
    if (out[zone] !== undefined) continue
    out[zone] = { id: bet.id, stake: bet.betAmount }
  }
  return out
}
